import { modelHelperModule } from './model-helper';
import { compareIgnoreCase } from '../utils/string-utils';
import { getFileLanguage, splitPath } from '../utils/file-utils/file-utils';
import { defineValue } from '../utils/object-utils/object-utils';
import { getExtension } from '../constants/languages';

/**
 * Converts a layout string to layout properties
 * @see layoutString
 * @param {string} layout
 * @returns {{tabindex: number, active: boolean, tabset: string}}
 */
export const layoutProps = (layout) => {
  const [, tabset, tabindex, active] =
    (layout && layout.match(/^(\w+):(\d+)(?::(\d))?/)) || [];
  return { tabset, tabindex: +tabindex, active: !!+active };
};

/**
 * Converts layout data into a string, or returns null if there is no tabset
 * @see layoutProps
 * @param {string} tabset
 * @param {number} tabindex
 * @param {boolean} active
 * @returns {string|null}
 */
export const layoutString = ({ tabset, tabindex, active }) => {
  return tabset ? `${tabset}:${+tabindex || 0}${active ? ':1' : ''}` : null;
};

/**
 * Configures a file with initial layout data
 * @param {CodeFile} file - File to configure
 * @param {boolean} always - If true, always override the file's layout data
 * @param {boolean} lockedLayout - If true, set the file's layout data if missing
 * @param {string?} initialLayout - Layout data, usually provided by file.initialLayout. {@see layoutString} for more details
 */
export const configureInitiallyOpenFile = ({
  file,
  always,
  lockedLayout,
  initialLayout = file.initialLayout,
}) => {
  const { tabset, tabindex, active } = layoutProps(
    file.hidden ? '' : initialLayout,
  );
  // save default tabset if available
  file.lastTabset = tabset || file.lastTabset || file.defaultTabset;
  file.defaultTabset = tabset;
  file.defaultTabindex = tabindex;
  file.defaultActive = active;
  if (always || (lockedLayout && !file.tabset && tabset)) {
    file.tabset = tabset;
    file.tabindex = tabindex;
    file.active = active;
  }
};

export const codeFileSubclasses = [
  'AdvancedCodeChallengeFile',
  'AdvancedCodeChallengeReferenceFile',
  'AdvancedCodeSolutionFile',
];

export const SOLUTION_FILE_CHANGE_KEYS = ['path', 'contents', 'cursor'];
export const SOLUTION_FILE_COPY_KEYS = [
  'id',
  'challengeFileId',
  'access',
  'deletable',
  'directory',
  'includeOnSubmit',
  'includeOnTest',
  'testGroups',
  'initialLayout',
  'field',
  'isClassicCode',
].concat(SOLUTION_FILE_CHANGE_KEYS);

function addOptionProperties(modelConfig, props) {
  props.forEach((prop) => {
    const optionKeySafe = prop.toLowerCase();
    modelConfig.computed[prop] = {
      get() {
        return (this.options || {})[optionKeySafe];
      },
      set(val) {
        this.options = this.options || {};
        this.options[optionKeySafe] = val;
      },
      enumerable: false,
    };
  });
}

function addHiddenProperties(modelConfig, props) {
  props.forEach((prop) => {
    modelConfig.computed[prop] = {
      get() {
        return (this.$hidden || {})[prop];
      },
      set(val) {
        if (!this.$hidden) {
          defineValue(this, '$hidden', {});
        }
        this.$hidden[prop] = val;
      },
      enumerable: false,
    };
  });
}

export const codeFileModelModule = angular
  .module('Q.models.code-file', [modelHelperModule])

  .factory('CodeFile', function (ModelHelper) {
    // shared functions for CodeFiles
    function define(modelConfig) {
      // property getters & setters
      addOptionProperties(modelConfig, [
        'collapsed',
        'tabset',
        'tabindex',
        'active',
        'closeable',
      ]);
      addHiddenProperties(modelConfig, [
        '$deleted',
        'defaultTabset',
        'defaultTabindex',
        'defaultActive',
        'lastTabset',
        'ignored',
        'parentAccess',
        'tabClasses',
        '$readOnly',
        'selections',
        '$interact',
        '$hideFromInteract',
      ]);

      const computed = modelConfig.computed;

      if (!computed.readOnly) {
        computed.readOnly = {
          get() {
            return this.$deleted;
          },
        };
      }
      computed.$markdown = {
        get() {
          return /\.(md|mark(down)?)$/.test(this.path);
        },
      };
      computed.$markdownReadOnly = {
        get() {
          return (
            this.$markdown &&
            this.$type === 'AdvancedCodeSolutionFile' &&
            this.$access &&
            this.$access !== 'readwrite' &&
            !this.$interact
          );
        },
      };
      computed.$access = {
        get() {
          let access = this.$interactMode ? 'readwrite' : this.access;
          if (access !== 'hidden') {
            const parentAccess = this.parentAccess;
            const parentHiding =
              parentAccess === 'restricted' || parentAccess === 'hidden';
            if (parentHiding) {
              // allows the parent access level to override this access level
              access = 'hidden';
            } else if (
              parentAccess === 'readonly' &&
              access === 'readwrite' &&
              this.directory
            ) {
              access = 'readonly';
            }
          }
          return access;
        },
        set(val) {
          // set the requested value, even though we may end up with something different based
          // on parent rules
          this.access = val;
        },
      };
      computed.$deletable = {
        get() {
          let deletable = this.deletable || false;
          if (deletable) {
            const parentAccess = this.parentAccess;
            if (parentAccess && parentAccess !== 'readwrite') {
              deletable = false;
            }
          }
          return deletable;
        },
        set(val) {
          // set the requested value, even though we may end up with something different based
          // on parent rules
          this.deletable = val;
        },
      };
      computed.language = {
        get() {
          return getFileLanguage(this);
        },
      };

      modelConfig.afterCreateInstance = (resource, files) => {
        if (!Array.isArray(files)) files = [files];
        files.forEach((file) => {
          file.$type = modelConfig.name;
        });
      };

      return ModelHelper.defineResource(modelConfig);
    }

    return {
      define,
      configureAccessLevels(files) {
        const parentRules = {};
        files
          .slice()
          // sorting ensures we'll go through them in the right order
          .sort((a, b) => compareIgnoreCase(a.path, b.path))
          .forEach((f) => {
            f.parentAccess = null;
            // try to set the parent access if there is any
            let parentPath = splitPath(f.path).pathParts;
            while (parentPath.length) {
              const parentAccess = parentRules[parentPath.join('/')];
              if (parentAccess) {
                f.parentAccess = parentAccess;
                break;
              }
              parentPath.pop();
            }
            // store the new $access (based on parent access)
            parentRules[f.path] = f.$access;
          });
      },
    };
  })

  .factory('AdvancedCodeChallengeFile', function (CodeFile) {
    const modelConfig = {
      name: 'AdvancedCodeChallengeFile',
      endpoint: 'advanced_code_challenge_files',

      cleanAfterDestroy: {
        parent: 'Challenge',
        collection: 'files',
      },

      meta: {
        defaults: {
          access: 'readwrite',
          accessLocked: false,
          codeSimilarity: false,
          contents: '',
          deletable: true,
          directory: false,
          includeOnSubmit: true,
          includeOnTest: true,
          initialLayout: null,
          options: {},
          path: '',
          testGroups: [],
        },
      },

      computed: {
        candidateCanOpen: {
          get() {
            return this.$access === 'readwrite' || this.$access === 'readonly';
          },
        },
      },
      methods: {},
    };

    addHiddenProperties(modelConfig, ['hasReference', 'wasDeletable']);

    return CodeFile.define(modelConfig);
  })

  .factory('AdvancedCodeChallengeReferenceFile', function (CodeFile) {
    const modelConfig = {
      name: 'AdvancedCodeChallengeReferenceFile',
      endpoint: 'advanced_code_challenge_reference_files',

      cleanAfterDestroy: {
        parent: 'Challenge',
        collection: 'referenceFiles',
      },

      meta: {
        defaults: {
          contents: '',
          directory: false,
          options: {},
          path: '',
          testGroups: [],
        },
      },

      computed: {},
      methods: {
        reference: true,
      },
    };

    addHiddenProperties(modelConfig, ['hasReference', 'notAllowed']);

    return CodeFile.define(modelConfig);
  })

  .factory('AdvancedCodeSolutionFile', function (DS, CodeFile) {
    const modelConfig = {
      name: 'AdvancedCodeSolutionFile',
      endpoint: 'advanced_code_solution_files',

      cleanAfterDestroy: {
        parent: 'Solution',
        collection: 'files',
      },

      computed: {
        interactId: {
          get() {
            if (this.field) {
              return `${this.field}.${getExtension(this.path)}`;
            } else {
              return this.path;
            }
          },
        },
        hidden: {
          get() {
            return !this.$reviewMode && this.$access === 'hidden';
          },
        },
        disabled: {
          get() {
            return (
              !this.$reviewMode &&
              (this.$access === 'restricted' || this.$access === 'hidden')
            );
          },
        },
        alwaysReadOnly: {
          get() {
            return (
              this.$reviewMode || this.$deleted || this.$access !== 'readwrite'
            );
          },
        },
        readOnly: {
          get() {
            return this.alwaysReadOnly || this.$readOnly;
          },
        },
        alwaysLocked: {
          get() {
            return (
              this.disabled ||
              this.alwaysReadOnly ||
              this.lockedLayout ||
              !this.$deletable
            );
          },
        },
        locked: {
          get() {
            return (
              this.disabled ||
              this.readOnly ||
              this.lockedLayout ||
              !this.$deletable
            );
          },
        },
        directoryLocked: {
          get() {
            return this.directory && (this.disabled || this.readOnly);
          },
        },
      },

      methods: {
        expirable: true,
        readWriteCopy() {
          const file = this;
          const props = {
            options: {},
            // override read/write
            $hideFromInteract: file.$hideFromInteract || file.disabled,
            $interactMode: true,
          };
          SOLUTION_FILE_COPY_KEYS.forEach((k) => {
            props[k] = file[k];
          });
          const fileCopy = DS.createInstance('AdvancedCodeSolutionFile', props);
          if (this.isClassicCode) {
            // have to write back to parent model
            Object.defineProperty(fileCopy, 'contents', {
              get() {
                return file.contents;
              },
              set(val) {
                file.contents = val;
              },
            });
          }
          return fileCopy;
        },
        readOnlyCopy() {
          const props = {
            options: {},
            $reviewMode: true,
          };
          SOLUTION_FILE_COPY_KEYS.forEach((k) => {
            props[k] = this[k];
          });
          return DS.createInstance('AdvancedCodeSolutionFile', props);
        },
        changesIgnored() {
          return (
            this.restricted || (this.includeOnSubmit && this.hasChallengeFile)
          );
        },
        ignoredWarningTooltip() {
          return `You may edit this file for your own tests, but \
<strong>${this.changesIgnored() ? 'changes to this file are' : 'this file is'} not included when assessing your code.</strong>\
<br>(Your edits will still be saved, and can be viewed by anyone reviewing your code.)`;
        },
      },

      staticMethods: {
        /**
         * Returns a filtered and sorted list of files to initially show
         * @param {Array<CodeFile>} files - Files to filter and sort
         * @returns {Array<CodeFile>} - Filtered and sorted list
         */
        initialLayout(files) {
          return files
            .filter(
              (f) =>
                !f.directory &&
                f.initialLayout &&
                f.$access !== 'hidden' &&
                f.$access !== 'restricted',
            )
            .sort((a, b) => {
              // ensure readwrite files are opened before readonly
              return a.$access === b.$access
                ? compareIgnoreCase(a.path, b.path)
                : a.$access === 'readwrite'
                  ? -1
                  : 1;
            });
        },

        /**
         * Configures the initial layout for each file
         * @param {Array<CodeFile>} files - Array of files
         * @param {boolean} always - If true, always set the layout, even if the file has a different one stored
         * @param {boolean} lockedLayout - If true, set the layout if missing from the file
         * @param {multiFileChallengeService} mfcs - Used to provide a default layout if we're missing all layouts
         * @returns {Array<CodeFile>}
         */
        configureInitialLayout(files, { always, lockedLayout, mfcs } = {}) {
          let initialLayoutFiles = this.initialLayout(files);
          if (initialLayoutFiles.length === 0 && mfcs) {
            initialLayoutFiles = [mfcs.ideSettingsFile];
          }
          initialLayoutFiles.forEach((file) => {
            configureInitiallyOpenFile({
              file,
              always,
              lockedLayout,
            });
          });
          return initialLayoutFiles;
        },
      },

      meta: {
        defaults: {
          access: 'readwrite',
          contents: '',
          deletable: true,
          directory: false,
          includeOnSubmit: true,
          includeOnTest: true,
          options: {},
          path: '',
          testGroups: [],
        },
      },
    };

    addHiddenProperties(modelConfig, [
      'canReset',
      'unchanged',
      'ignored',
      'restricted',
      'hasChallengeFile',
      'lockedLayout',
      '$interactMode',
    ]);

    return CodeFile.define(modelConfig);
  });
